// Site Content — client-side registry for the Website Content Manager.
//
// Loads every currently-published content value from `site_content_current`
// once (cached in React Query), keeps it fresh via realtime, and exposes a
// simple hook + component so any page can render editable copy while falling
// back to a hard-coded string if the key hasn't been migrated yet.
//
// This is the ONE place components read website copy from. Never inline a
// literal string that a non-technical admin might want to change — register it
// as a content key and use <Content ck="..." fallback="..." />.

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ContentRow {
  key_id: string;
  key: string;
  page: string;
  section: string | null;
  label: string;
  description: string | null;
  fallback_text: string;
  variable_schema: Array<{ name: string; label?: string }>;
  is_shared: boolean;
  content_type: "plain" | "markdown" | "html";
  version_id: string | null;
  value: string | null;
  published_at: string | null;
}

const QUERY_KEY = ["site-content-current"] as const;

async function fetchAll(): Promise<ContentRow[]> {
  const { data, error } = await supabase
    .from("site_content_current" as never)
    .select("*");
  if (error) throw error;
  return (data ?? []) as unknown as ContentRow[];
}

/** Substitute {{name}} placeholders with values from `vars`. Unknown names stay literal. */
export function renderContent(
  template: string,
  vars?: Record<string, string | number | null | undefined>,
): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (m, name) => {
    const v = vars[name];
    return v === undefined || v === null ? m : String(v);
  });
}

/**
 * Read a piece of editable website copy.
 *   - Returns the currently-published value when the key exists.
 *   - Falls back to `fallback` (or the DB-registered fallback_text) otherwise,
 *     so a not-yet-migrated string still renders.
 */
export function useContent(
  key: string,
  fallback: string,
  vars?: Record<string, string | number | null | undefined>,
): string {
  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchAll,
    staleTime: 60_000,
  });

  useEffect(() => {
    // Set up realtime once per client — the channel is shared via singleton.
    ensureRealtime();
  }, []);

  const row = useMemo(
    () => (data ?? []).find((r) => r.key === key),
    [data, key],
  );

  const raw = row?.value ?? row?.fallback_text ?? fallback;
  return renderContent(raw, vars);
}

/** Returns every registered content row, or [] while loading. */
export function useAllContent(): ContentRow[] {
  const { data } = useQuery({ queryKey: QUERY_KEY, queryFn: fetchAll, staleTime: 60_000 });
  return data ?? [];
}

let realtimeStarted = false;
function ensureRealtime() {
  if (realtimeStarted || typeof window === "undefined") return;
  realtimeStarted = true;
  // Invalidate the cache when versions or keys change.
  const channel = supabase
    .channel("site-content-live")
    .on("postgres_changes", { event: "*", schema: "public", table: "site_content_versions" }, () => {
      import("@tanstack/react-query").then(({ QueryClient }) => {
        // A shared client is imported lazily via a global lookup.
        const client = (window as unknown as { __RQ_CLIENT?: unknown }).__RQ_CLIENT;
        if (client && typeof (client as { invalidateQueries?: (opts: unknown) => void }).invalidateQueries === "function") {
          (client as { invalidateQueries: (opts: unknown) => void }).invalidateQueries({ queryKey: QUERY_KEY });
        }
      });
    })
    .subscribe();
  // No teardown — content is app-wide.
  void channel;
}
